// AI Generated Component
import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { StatisticsDateComparisonService } from '@monsido/modules/statistics/services/date-comparison/statistics-date-comparison.service';
import { MonModalService } from 'app/modules/modals/services/mon-modal/mon-modal.service';
import moment from 'moment';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { StatisticsExportService } from '@client/app/pages/statistics/services/statistics-export.service';

@Component({
    selector: 'statistics-pages-documents-without-downloads',
    standalone: false,
    templateUrl: './documents-without-downloads.component.html',
})
export class StatisticsPagesDocumentsWithoutDownloadsComponent implements OnInit {
    loading: boolean = false;
    comparisonEnabled: boolean | null = null;
    compareDate: any = { startDate: null, endDate: null };
    noPagesResultMessage: string = '';
    noDocumentsResultMsg: string = '';
    page: number = 1;
    pageSize: string = '10';

    domain: any;
    date: any;
    dateFormat: string = '';
    search: string = '';
    exports: any[] = [];
    documents: any[] = [];
    promise: any;

    constructor (
        private statsRepoService: StatsRepoService,
        private stateService: StateService,
        private paramService: ParamService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private dateRangeService: DateRangeService,
        private statisticsDateComparisonService: StatisticsDateComparisonService,
        private monModalService: MonModalService,
        private statisticsExportService: StatisticsExportService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    activate () {
        this.domain = this.sessionService.domain;
        this.search = '';
        this.date = this.dateRangeService.setRangeDates();
        this.dateFormat = 'ddd, D. MMM YYYY';
        this.compareDate = { startDate: null, endDate: null };

        this.noPagesResultMessage = this.translateService.getString('No documents found');
        this.noDocumentsResultMsg = this.translateService.getString('No documents found');

        this.setExports();
        this.getPage();
    }

    validateDate = this.statisticsDateComparisonService.validateDate;

    updateCompareDate (e: any): Promise<string> | void {
        const isFutureDates = this.statisticsDateComparisonService.isFutureDate(e.startDate) ||
            this.statisticsDateComparisonService.isFutureDate(e.endDate);

        this.comparisonEnabled = e.startDate && e.endDate && !isFutureDates;

        if (isFutureDates) {
            return this.monModalService.alert(
                this.translateService.getString(
                    'The start and end date cannot be in the future, please enter the compare dates again',
                ),
            );
        }

        if (this.comparisonEnabled) {
            this.compareDate.startDate = e.startDate;
            this.compareDate.endDate = e.endDate;
            this.pageSize = '10';
            this.page = 1;
            this.getComparisonData();
        }
    }

    getComparisonData () {
        this.loading = true;
        const dateFormat = 'YYYY-MM-DD';
        const currentParams = {
            limit: this.pageSize,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            from: this.date.startDate?.format(dateFormat),
            to: this.date.endDate?.format(dateFormat),
            search: this.search,
        };
        const compareParams = {
            ...currentParams,
            from: this.compareDate.startDate?.format(dateFormat),
            to: this.compareDate.endDate?.format(dateFormat),
        };

        this.promise = Promise.all([
            this.statsRepoService.getPagesDocumentsWithoutDownloads(currentParams, contextTokens.NO_PARSE_SEARCH),
            this.statsRepoService.getPagesDocumentsWithoutDownloads(compareParams, contextTokens.NO_PARSE_SEARCH),
        ]).then(([currentDocuments, compareDocuments]) => {
            this.documents = currentDocuments.map((doc: any) => {
                const compareDoc = compareDocuments.find((cd: any) => cd.url === doc.url);
                return {
                    ...doc,
                    compare: compareDoc || { downloads: 0 },
                };
            });
        }).finally(() => {
            this.loading = false;
        });
    }

    updateDateRange () {
        if (this.date.startDate && this.date.endDate) {
            this.paramService.setParams({
                from: moment(this.date.startDate),
                to: moment(this.date.endDate),
                search: this.search,
            });
        }
        this.setExports();
        this.getPage();
    }

    onSearch (search: string) {
        this.search = search;
        this.page = 1;
        if (this.comparisonEnabled) {
            this.getComparisonData();
        } else {
            this.getPage();
        }
    }

    getPage () {
        const dateFormat = 'YYYY-MM-DD';
        const params = {
            limit: this.pageSize,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            from: this.date.startDate?.format(dateFormat),
            to: this.date.endDate?.format(dateFormat),
            search: this.search,
        };

        this.loading = true;
        this.promise = this.statsRepoService.getPagesDocumentsWithoutDownloads(params, contextTokens.NO_PARSE_SEARCH)
            .then((documents: any[]) => {
                this.documents = documents;
            }).finally(() => {
                this.loading = false;
            });
    }

    onPageChangeLocal (page: number) {
        this.page = page;
        if (this.comparisonEnabled) {
            this.getComparisonData();
        } else {
            this.getPage();
        }
    }

    onPageSizeChange (pageSize: number) {
        this.pageSize = pageSize.toString();
        this.page = 1;
        if (this.comparisonEnabled) {
            this.getComparisonData();
        } else {
            this.getPage();
        }
    }

    setExports () {
        this.exports = this.statisticsExportService.getDocumentWithoutDownloadsExportDefinitions(this.date.startDate, this.date.endDate);
    }
}
